import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { adminAccessTokenStorage, registerUnauthorizedHandler } from '../../api/client';
import { AdminAuthContext, type AdminAuthStatus } from './useAdminAuth';

interface AdminAuthProviderProps {
  children: ReactNode;
}

// PARA-151 (AR-10): admin_id хранится в sessionStorage рядом с токеном —
// переживает перезагрузку страницы так же, как сам токен.
const ADMIN_ID_STORAGE_KEY = 'admin_id';

function readStoredAdminId(): number | null {
  const raw = sessionStorage.getItem(ADMIN_ID_STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : null;
}

// PARA-106 §4 (UI-архитектура) — статус выводится из наличия admin_access_token
// в sessionStorage; любой 401/403 от /admin/* (client.ts onUnauthorized) сбрасывает
// сессию и очищает React Query cache.
export function AdminAuthProvider({ children }: AdminAuthProviderProps): JSX.Element {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AdminAuthStatus>(() =>
    adminAccessTokenStorage.get() ? 'authenticated' : 'unauthenticated',
  );
  const [adminId, setAdminId] = useState<number | null>(readStoredAdminId);

  const logout = useCallback(() => {
    adminAccessTokenStorage.clear();
    sessionStorage.removeItem(ADMIN_ID_STORAGE_KEY);
    queryClient.clear();
    setAdminId(null);
    setStatus('unauthenticated');
  }, [queryClient]);

  const login = useCallback((token: string, id: number) => {
    adminAccessTokenStorage.set(token);
    sessionStorage.setItem(ADMIN_ID_STORAGE_KEY, String(id));
    setAdminId(id);
    setStatus('authenticated');
  }, []);

  useEffect(() => {
    registerUnauthorizedHandler(logout);
  }, [logout]);

  return (
    <AdminAuthContext.Provider value={{ status, adminId, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}
